import { Button, MessageBar, MessageBarBody, Tooltip, makeStyles, tokens } from '@fluentui/react-components';
import { Checkmark16Regular, Dismiss16Regular, Globe16Regular, Router20Regular } from '@fluentui/react-icons';
import { useState } from 'react';
import type { Device, SiteHost } from '../../api/types';
import { useAuth } from '../../auth/AuthProvider';
import { StatePill } from '../../components/StatePill';
import { useShell } from '../../lib/shell';
import { errorText } from '../../lib/useRelay';
import { vt } from '../../theme';
import { statusCells } from '../devices/model';
import { ago, duration } from '../overview/model';
import { routeError, siteRoutes, siteStatus, withRoute } from './model';

const useStyles = makeStyles({
  card: {
    display: 'grid',
    gridTemplateColumns: '44px minmax(0, 1fr) auto auto',
    columnGap: '16px',
    padding: '20px',
    borderRadius: '8px',
    border: `1px solid ${vt.surface2}`,
    boxSizing: 'border-box',
  },
  iconBox: {
    position: 'relative',
    width: '44px',
    height: '44px',
    borderRadius: '11px',
    backgroundColor: vt.surface3,
    border: `1px solid ${vt.surface2}`,
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: vt.fg2,
  },
  liveDot: {
    position: 'absolute',
    top: '0',
    right: '0',
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: vt.green,
    boxShadow: `0 0 0 2px ${vt.pane}`,
  },
  main: { display: 'flex', flexDirection: 'column', minWidth: 0 },
  titleRow: { display: 'flex', alignItems: 'center', columnGap: '14px' },
  name: { margin: 0, fontSize: '16px', fontWeight: tokens.fontWeightSemibold, color: vt.fg1 },
  sub: { marginTop: '6px', fontSize: '11px', color: vt.fg3 },
  colLabel: { fontSize: '11px', color: vt.fg3, marginTop: '22px' },
  headline: { fontFamily: tokens.fontFamilyMonospace, fontSize: '16px', color: vt.fg1, marginTop: '6px' },
  hint: { fontSize: '11px', color: vt.fg3, marginTop: '4px' },
  routes: { listStyleType: 'none', margin: '10px 0 0', padding: 0, display: 'flex', flexDirection: 'column', rowGap: '6px' },
  route: { display: 'flex', alignItems: 'center', columnGap: '10px', fontSize: '11px', color: vt.fg3 },
  routeCidr: { fontFamily: tokens.fontFamilyMonospace, fontSize: '12px', color: vt.fg1 },
  pending: { color: vt.orange },
  refused: { color: vt.red },
  facts: { display: 'grid', gridTemplateColumns: 'repeat(2, 140px)', rowGap: '14px', alignContent: 'start', margin: 0 },
  factLabel: { fontSize: '11px', color: vt.fg3 },
  factValue: { margin: '4px 0 0', fontFamily: tokens.fontFamilyMonospace, fontSize: '12px', color: vt.fg1 },
  side: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', rowGap: '12px', minWidth: '180px' },
  traffic: { fontFamily: tokens.fontFamilyMonospace, fontSize: '10px', color: vt.fg4 },
  error: { gridColumn: '1 / -1', marginTop: '14px' },
});

interface Props {
  site: Device;
  /** null while loading. */
  hosts: SiteHost[] | null;
  allSites: Device[];
  now: number;
  /** After a route change landed. */
  onChanged: () => Promise<void>;
}

/** Mockup 08's site card: the route it carries leads, then its own facts. */
export function SiteCard({ site, hosts, allSites, now, onChanged }: Props) {
  const s = useStyles();
  const { api } = useAuth();
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const status = siteStatus(site, now);
  const routes = siteRoutes(site);
  const info = site.site;
  const { statuses, live } = useShell();
  // The site's own view: up = what the router sent (tx), down = what it received (rx).
  const cells = statusCells(site.id, statuses, live, now);

  const change = async (route: string, next: string[]) => {
    setBusy(route);
    setError(null);
    try {
      await api.setDeviceRoutes(site.id, next);
      await onChanged();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(null);
    }
  };

  const sub = [info?.model, info?.firmware, info?.agentVersion && `vipper ${info.agentVersion}`].filter(Boolean).join(' · ');
  const headline = site.routes.length ? site.routes.join(', ') : 'no approved route';
  const reported = info?.reportedAt ? `last host report ${ago(info.reportedAt, now)}` : 'no host report yet';

  return (
    <div className={s.card} role="group" aria-label={`Site ${site.name}`}>
      <div className={s.iconBox} aria-hidden>
        <Router20Regular />
        {status === 'online' && <span className={s.liveDot} />}
      </div>

      <div className={s.main}>
        <div className={s.titleRow}>
          <h2 className={s.name}>{site.name}</h2>
          <Tooltip content={reported} relationship="description">
            <span>
              {status === 'online' ? (
                <StatePill state="running" label="Online" />
              ) : status === 'silent' ? (
                <StatePill state="stopped" label="Not reporting" />
              ) : (
                <StatePill state="attention" label="Waiting for the router" />
              )}
            </span>
          </Tooltip>
        </div>
        {sub && <div className={s.sub}>{sub}</div>}

        <div className={s.colLabel}>Advertises</div>
        <div className={s.headline} data-testid="site-routes">
          {headline}
        </div>
        <div className={s.hint}>
          {hosts === null ? 'counting hosts…' : `${hosts.length} host${hosts.length === 1 ? '' : 's'} reachable with no agent`}
        </div>

        {routes.some((r) => r.state !== 'approved') && (
          <ul className={s.routes} aria-label="Routes to review">
            {routes
              .filter((r) => r.state !== 'approved')
              .map(({ route, state }) => {
                const refused = state === 'pending' ? routeError(route, site.id, allSites) : null;
                return (
                  <li key={route} className={s.route} data-route={route} data-state={state}>
                    <span className={s.routeCidr}>{route}</span>
                    {state === 'pending' ? (
                      <>
                        <span className={refused ? s.refused : s.pending}>{refused ?? 'advertised · not approved'}</span>
                        <Button
                          size="small"
                          shape="circular"
                          appearance="primary"
                          icon={<Checkmark16Regular />}
                          disabled={busy !== null || refused !== null}
                          onClick={() => void change(route, withRoute(site.routes, route))}
                        >
                          Approve
                        </Button>
                      </>
                    ) : (
                      <>
                        <span className={s.pending}>approved · no longer advertised by the site</span>
                        <Button
                          size="small"
                          shape="circular"
                          appearance="outline"
                          icon={<Dismiss16Regular />}
                          disabled={busy !== null}
                          onClick={() => void change(route, site.routes.filter((r) => r !== route))}
                        >
                          Withdraw
                        </Button>
                      </>
                    )}
                  </li>
                );
              })}
          </ul>
        )}
      </div>

      <dl className={s.facts}>
        {(
          [
            ['Overlay address', site.overlayIp],
            ['WAN', info?.wanIp ?? '–'],
            ['Uptime', info?.uptimeSeconds != null ? duration(info.uptimeSeconds * 1000) : '–'],
            ['Last handshake', cells.handshake],
          ] as const
        ).map(([k, v]) => (
          <div key={k}>
            <dt className={s.factLabel}>{k}</dt>
            <dd className={s.factValue}>{v}</dd>
          </div>
        ))}
      </dl>

      <div className={s.side}>
        <span className={s.traffic}>
          {cells.tx} up · {cells.rx} down
        </span>
        <Button
          as="a"
          href={`http://${site.overlayIp}/`}
          target="_blank"
          rel="noreferrer"
          size="small"
          shape="circular"
          appearance="outline"
          icon={<Globe16Regular />}
        >
          Open LuCI
        </Button>
      </div>

      {error && (
        <MessageBar intent="error" className={s.error}>
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}
    </div>
  );
}
